import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Package, DollarSign, TrendingUp, Building2, Plus, Trash2, Rocket, Presentation, FileText } from "lucide-react";
import { fmtBRL } from "@/lib/api";

function StatCard({ label, value, icon: Icon, color, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
          <div className="text-2xl md:text-3xl font-extrabold mt-1">{value}</div>
        </div>
        <div className={`${color} p-3 rounded-xl text-white`}><Icon className="w-5 h-5" /></div>
      </CardContent>
    </Card>
  );
}

/**
 * Aba "Consolidado" do Dashboard — visível só para Super Admin.
 * Mostra ranking + charts + ações administrativas (Modo Apresentação, PDF Estado Inicial, Nova Empresa, Preparar Lançamento, Excluir Empresa).
 */
export function DashboardConsolidado({
  empresas, cons, filters, setFilters,
  onNovaEmpresa, onModoApresentacao, onBaixarPdfApresentacao,
  onLaunchEmpresa, onPurgeEmpresa,
}) {
  return (
    <>
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="text-sm text-muted-foreground">Comparativo entre todas as empresas cadastradas</div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={onModoApresentacao} variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="btn-modo-apresentacao">
            <Presentation className="w-4 h-4 mr-2" />Modo Apresentação
          </Button>
          <Button onClick={onBaixarPdfApresentacao} variant="outline" className="border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="btn-baixar-pdf-apresentacao" title="Baixa PDF com o estado atual (empresas, cadastros, Super Admins)">
            <FileText className="w-4 h-4 mr-2" />PDF Estado Inicial
          </Button>
          <Button onClick={onNovaEmpresa} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-nova-empresa">
            <Plus className="w-4 h-4 mr-2" />Nova Empresa
          </Button>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Período (opcional)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-xs">De</Label>
            <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} data-testid="cons-start"/></div>
          <div><Label className="text-xs">Até</Label>
            <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} data-testid="cons-end"/></div>
          <div className="flex items-end">
            <Button variant="ghost" onClick={() => setFilters({ ...filters, start: "", end: "" })} className="w-full">Limpar</Button>
          </div>
        </CardContent>
      </Card>

      {!cons ? <div className="text-muted-foreground p-6">Carregando consolidado...</div> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label="Empresas" value={empresas.length} icon={Building2} color="bg-orange-600" testId="cons-empresas"/>
            <StatCard label="Total Geral" value={cons.totais.count} icon={Package} color="bg-blue-600" testId="cons-total"/>
            <StatCard label="Faturamento Geral" value={fmtBRL(cons.totais.faturamento)} icon={DollarSign} color="bg-emerald-600" testId="cons-fat"/>
          </div>

          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-orange-600"/>Ranking por Faturamento</CardTitle></CardHeader>
            <CardContent className="space-y-3" data-testid="ranking-list">
              {cons.empresas.length === 0 ? <div className="text-muted-foreground py-4">Sem dados</div> : cons.empresas.map((e, i) => (
                <div key={e.empresa_id} className="flex items-center gap-4 p-3 border border-orange-100 rounded-lg bg-orange-50/30" data-testid={`rank-${e.empresa_id}`}>
                  <div className="w-10 h-10 rounded-full bg-orange-600 text-white flex items-center justify-center font-extrabold text-lg shrink-0">{i + 1}</div>
                  {e.logo_base64
                    ? <img src={e.logo_base64} alt="" className="w-10 h-10 object-contain rounded bg-white"/>
                    : <div className="w-10 h-10 rounded bg-orange-100 flex items-center justify-center text-orange-600"><Building2 className="w-5 h-5"/></div>}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{e.empresa}</div>
                    <div className="text-xs text-muted-foreground">{e.count} entregas · {e.concluidas} concluídas · ticket médio {fmtBRL(e.ticket_medio)}</div>
                  </div>
                  <div className="text-right shrink-0 flex items-center gap-2">
                    <div>
                      <div className="text-lg font-extrabold text-emerald-700 mono">{fmtBRL(e.faturamento)}</div>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => onLaunchEmpresa({ id: e.empresa_id, nome: e.empresa, count: e.count })} data-testid={`btn-launch-empresa-${e.empresa_id}`} title={`Preparar "${e.empresa}" para lançamento — limpa entregas e entregadores mantendo cadastros`}>
                      <Rocket className="w-4 h-4 text-emerald-600" />
                    </Button>
                    {empresas.length > 1 && (
                      <Button size="icon" variant="ghost" onClick={() => onPurgeEmpresa({ id: e.empresa_id, nome: e.empresa, count: e.count })} data-testid={`btn-excluir-empresa-${e.empresa_id}`} title={`Excluir empresa "${e.empresa}"`}>
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            <Card><CardHeader><CardTitle>Entregas por Empresa</CardTitle></CardHeader>
              <CardContent style={{ height: 320 }}>
                <ResponsiveContainer><BarChart data={cons.empresas}>
                  <XAxis dataKey="empresa" fontSize={10} interval={0} angle={-20} textAnchor="end" height={70}/>
                  <YAxis fontSize={11}/>
                  <Tooltip/>
                  <Bar dataKey="count" fill="#EA580C" radius={[6, 6, 0, 0]}/>
                </BarChart></ResponsiveContainer>
              </CardContent>
            </Card>
            <Card><CardHeader><CardTitle>Ticket Médio por Empresa</CardTitle></CardHeader>
              <CardContent style={{ height: 320 }}>
                <ResponsiveContainer><BarChart data={cons.empresas}>
                  <XAxis dataKey="empresa" fontSize={10} interval={0} angle={-20} textAnchor="end" height={70}/>
                  <YAxis fontSize={11} tickFormatter={(v) => `R$${v.toFixed(0)}`}/>
                  <Tooltip formatter={(v) => fmtBRL(v)}/>
                  <Bar dataKey="ticket_medio" fill="#10B981" radius={[6, 6, 0, 0]}/>
                </BarChart></ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </>
  );
}
